import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { of, ReplaySubject, Subject } from 'rxjs';
import { catchError, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { CalendarService } from 'src/app/services/calendar/calendar.service';
import { ConfirmDeleteDialogComponent } from '../../dialogs/confirm-delete-dialog/confirm-delete-dialog.component';
import { CalendarConfig, CalendarTypes, GenericCalendarConfig, GoogleCalendarConfig, ICalendarsConfigurationData, IOrganisation } from '@bciti/types';
import * as _ from 'lodash';
import { ActivatedRoute } from '@angular/router';

@Component({
    selector: 'app-calendar-form',
    templateUrl: './calendar-form.component.html',
    styleUrls: ['./calendar-form.component.scss'],
})
export class CalendarFormComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject();
    private update$ = new ReplaySubject();

    @Input() org: IOrganisation;
    orgTag: string;

    calendarTypesSelectOptions = Object.values(CalendarTypes);
    CalendarTypes = CalendarTypes;

    loading = false;
    syncLoading = false;
    syncResponse = '';

    initialCalendarsConfig: CalendarConfig<any>[];

    calendarsForm = this.fb.group({
        calendars: this.fb.array([]),
    });

    genericCalendarForm = new FormGroup({
        frUrl: new FormControl(undefined, [Validators.required, Validators.pattern(/^\S*$/)]),
        enUrl: new FormControl(undefined, [Validators.required, Validators.pattern(/^\S*$/)]),
    });

    googleCalendarForm = new FormGroup({
        client_id: new FormControl(undefined, [Validators.required]),
        project_id: new FormControl(undefined, [Validators.required]),
        auth_uri: new FormControl(undefined, [Validators.required]),
        token_uri: new FormControl(undefined, [Validators.required]),
        auth_provider_x509_cert_url: new FormControl(undefined, [Validators.required]),
        client_secret: new FormControl(undefined, [Validators.required]),
        redirect_uris: new FormControl([], [Validators.required]),
        token: new FormGroup({
            access_token: new FormControl(undefined, [Validators.required]),
            expires_in: new FormControl(undefined, [Validators.required]),
            token_type: new FormControl(undefined, [Validators.required]),
            scope: new FormControl(undefined, [Validators.required]),
            refresh_token: new FormControl(undefined, [Validators.required]),
        }),
    });

    get calendars(): FormArray {
        return this.calendarsForm.get('calendars') as FormArray;
    }

    constructor(private route: ActivatedRoute, private calendarService: CalendarService, private toastrService: ToastrService, private dialog: MatDialog, private fb: FormBuilder) {}

    get getTitle(): string {
        return this.org ? `Calendars : ${this.org.name}` : 'Calendars';
    }

    ngOnInit(): void {
        this.route.params.subscribe((param) => {
            if (param.tag) {
                this.orgTag = param.tag;
                this.update$
                    .pipe(
                        takeUntil(this.destroy$),
                        tap(() => (this.loading = true)),
                        switchMap(() =>
                            this.calendarService.getCalendarConfiguration(param.tag).pipe(
                                takeUntil(this.destroy$),
                                tap((config) => {
                                    this.initialCalendarsConfig = _.cloneDeep(config);
                                    this.initCalendarsForm(config);
                                    this.loading = false;
                                }),
                                catchError((error) => {
                                    this.toastrService.error(error.error.messages, `${error.error.statusCode}: ${error.error.error}`);
                                    return of('');
                                }),
                            ),
                        ),
                    )
                    .subscribe();

                this.update$.next();
            }
        });
    }

    initCalendarsForm(config: CalendarConfig<any>[]) {
        if (!config.length) {
            this.addCalendar();
        } else {
            this.fillCalendarsForm(config);
        }
    }

    addCalendar(): void {
        this.calendars.push(this.createCalendarForm());
    }

    fillCalendarsForm(config: CalendarConfig<any>[]): void {
        while (this.calendars.length !== 0) {
            this.calendars.removeAt(0);
        }
        config.forEach((calendar) => {
            this.calendars.push(this.createCalendarForm(calendar));
        });
    }

    createCalendarForm(calendarValue?: GoogleCalendarConfig | GenericCalendarConfig): FormGroup {
        let name = calendarValue?.name ?? '';
        let type = calendarValue?.type ?? null;
        const calendarFormGroup = this.fb.group({
            name: [name, Validators.required],
            type: [type, Validators.required],
        });

        if (calendarValue) {
            this.updateCalendarFields(calendarFormGroup, calendarValue.type, calendarValue);
            return calendarFormGroup;
        }

        calendarFormGroup.valueChanges.subscribe(() => {
            this.calendarsForm.updateValueAndValidity();
        });
        return calendarFormGroup;
    }

    onSelectType(selectedType: CalendarTypes, formIndex: number, calendarValue: GoogleCalendarConfig | GenericCalendarConfig) {
        let calendarFormGroup = this.calendars.controls[formIndex] as FormGroup;
        calendarValue = {
            ...calendarValue,
            type: selectedType,
        } as any;
        this.updateCalendarFields(calendarFormGroup, selectedType, calendarValue);
    }

    updateCalendarFields(calendarFormGroup: FormGroup, type: CalendarTypes, calendarValue?: GoogleCalendarConfig | GenericCalendarConfig): void {
        Object.keys(calendarFormGroup.controls)
            .filter((key) => key !== 'name' && key !== 'type')
            .forEach((key) => {
                calendarFormGroup.removeControl(key);
            });

        if (type === CalendarTypes.GOOGLE) {
            Object.keys(this.googleCalendarForm.controls).forEach((key) => {
                calendarFormGroup.addControl(key, this.cloneFormGroup(this.googleCalendarForm.get(key)!));
            });
        } else {
            Object.keys(this.genericCalendarForm.controls).forEach((key) => {
                calendarFormGroup.addControl(key, this.cloneFormGroup(this.genericCalendarForm.get(key)!));
            });
        }

        if (calendarValue) {
            Object.keys(calendarValue).forEach((key) => {
                if (calendarFormGroup.get(key)) {
                    calendarFormGroup.get(key)?.patchValue(calendarValue[key]);
                    calendarFormGroup.get(key)?.updateValueAndValidity();
                }
            });
        }
        this.calendarsForm.updateValueAndValidity();
    }

    cloneFormGroup(control: AbstractControl): AbstractControl {
        if (control instanceof FormControl) {
            return new FormControl(control.value, control.validator, control.asyncValidator);
        } else if (control instanceof FormGroup) {
            const group = new FormGroup({});
            Object.keys(control.controls).forEach((key) => {
                group.addControl(key, this.cloneFormGroup(control.controls[key]));
            });
            return group;
        } else if (control instanceof FormArray) {
            const array = new FormArray([]);
            control.controls.forEach((ctrl) => {
                array.push(this.cloneFormGroup(ctrl));
            });
            return array;
        } else {
            throw new Error('Unsupported control type');
        }
    }

    removeCalendar(index: number): void {
        this.calendars.removeAt(index);
    }

    resetConfig(): void {
        this.calendars.clear();
        if (this.initialCalendarsConfig.length) {
            this.initCalendarsForm(this.initialCalendarsConfig);
        } else {
            this.addCalendar();
        }
    }

    handleSubmitClicked() {
        this.calendars.markAllAsTouched();
        if (this.calendarsForm.valid) {
            let calendarValue = this.calendarsForm.value;
            let payload = this.mapConfigPayload(calendarValue);
            this.submitForm(payload);
        }
    }

    mapConfigPayload(config: any): ICalendarsConfigurationData {
        let mappedCalendars = {
            ...config,
            calendars: config.calendars.map((calendar) => {
                if (calendar.type === 'GOOGLE' && calendar.token && typeof calendar.token.expires_in === 'string') {
                    return {
                        ...calendar,
                        token: {
                            ...calendar.token,
                            expires_in: Number(calendar.token.expires_in),
                        },
                    };
                }
                return calendar;
            }),
        };
        return mappedCalendars;
    }

    submitForm(payload: ICalendarsConfigurationData): void {
        this.loading = true;
        this.calendarService
            .updateCalendarConfiguration(this.orgTag, payload)
            .pipe(
                take(1),
                tap(() => this.update$.next()),
                tap(() => this.toastrService.success('Calendars was updated')),
                catchError((error) => {
                    this.toastrService.error(error.error.messages, `${error.error.status}`);
                    this.loading = false;
                    return of();
                }),
            )
            .subscribe();
    }

    // dont touch -->

    handleDeleteCalendar() {
        const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
            autoFocus: false,
            disableClose: true,
            hasBackdrop: true,
            width: '600px',
            data: {
                title: `Deleting ${this.org.name}'s calendar`,
                body: [`Are you sure you want to delete this org's calendar?`],
                loading: false,
            },
        });

        const confirmDeleteSub = dialogRef.componentInstance.confirm.pipe(takeUntil(this.destroy$)).subscribe((v) => {
            if (v) {
                dialogRef.componentInstance.data.loading = true;

                this.calendarService
                    .deleteCalendarConfiguration(this.org.bcitiId)
                    .pipe(take(1))
                    .subscribe(
                        (response) => {
                            dialogRef.close(true);
                            this.toastrService.success('Calendar was deleted');
                        },
                        (error) => {
                            dialogRef.componentInstance.data.loading = false;
                            console.log(error);
                            this.toastrService.error(error.message, `${error.status}`);
                        },
                    );
            }
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((v) => {
                if (v) {
                    this.update$.next();
                }
                confirmDeleteSub.unsubscribe();
            });
    }

    removeRedirectUri(uri: string, calendarIndex: number) {
        let calendarGroup = this.calendars.at(calendarIndex) as FormGroup;
        const set = new Set(calendarGroup.controls.redirect_uris.value);
        set.delete(uri);
        calendarGroup.patchValue({ redirect_uris: Array.from(set) });
    }

    addRedirectUri(event: MatChipInputEvent, calendarIndex: number) {
        let calendarGroup = this.calendars.at(calendarIndex) as FormGroup;
        const value = event.value.trim() || '';
        if (value) {
            const set = new Set(calendarGroup.controls.redirect_uris.value);
            set.add(value);
            calendarGroup.patchValue({ redirect_uris: Array.from(set) });
            event.input.value = '';
        }
    }

    syncCalendar() {
        if (this.initialCalendarsConfig) {
            this.syncLoading = true;
            this.calendarService
                .synchronizeOrgCalendar(this.orgTag)
                .pipe(
                    take(1),
                    tap((res) => {
                        this.syncResponse = res;
                        this.toastrService.success('Calendar should be synchronized momentarily.', 'Calendar sync engaged!');
                        this.syncLoading = false;
                    }),
                    catchError((error) => {
                        this.toastrService.error(error.error.message, `${error.error.statusCode}`);
                        this.syncLoading = false;
                        return of();
                    }),
                )
                .subscribe();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
